/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import { jsx } from '@compiled/react';
import { bind } from 'bind-event-listener';

import { Popup, type TriggerProps } from '@atlaskit/popup';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';
import UFOSegment from '@atlaskit/react-ufo/segment';
import { KeyboardHighlightProvider } from '@atlassian/search-dialog';

import { onDropdownClosed } from '../../../common/utils/events/dropdown-closed';
import { onDropdownViewed } from '../../../common/utils/events/dropdown-viewed';
import { useAnalytics, useCommonAndPrivateAttributes } from '../../../controllers/analytics';
import { useDropdownPrefetch } from '../../../controllers/dropdown';
import { ErrorBoundary } from '../../../controllers/error-boundary';
import { useDropdown } from '../../../controllers/store/dropdown';
import { useSearchQuery } from '../../../controllers/store/search';

import { SearchDropdown } from './dropdown';
import { SearchTextField } from './textfield';

const textFieldBoxStyles = xcss({
	height: 'space.500',
	flexGrow: 1,
});

export const Input: () => JSX.Element = () => {
	const [showDropdown, { setShowDropdown }] = useDropdown();
	const inputBoxRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);

	const [searchQuery] = useSearchQuery();

	useDropdownPrefetch();

	const [_, setInputWidth] = useState(inputBoxRef.current?.offsetWidth);
	const [localQuery, setLocalQuery] = useState(searchQuery);

	const { fireAnalyticsEvent, fireAnalyticsScreenEvent } = useAnalytics();
	const { commonAttributes, nonPrivacySafeAttributes } = useCommonAndPrivateAttributes();

	useEffect(() => {
		const listener = () => setInputWidth(inputBoxRef.current?.offsetWidth);
		const unbind = bind(window, {
			type: 'resize',
			listener,
		});
		return unbind;
	}, []);

	const closeDropdown = useCallback(() => {
		if (showDropdown) {
			setShowDropdown(false);
			fireAnalyticsEvent(
				onDropdownClosed({
					commonAttributes,
					nonPrivacySafeAttributes,
				}),
			);
		}
	}, [
		showDropdown,
		setShowDropdown,
		fireAnalyticsEvent,
		commonAttributes,
		nonPrivacySafeAttributes,
	]);

	const trigger = useCallback(
		(triggerProps: TriggerProps) => {
			// When the SearchTextField receives a submit/enter
			const hasSubmitted = () => {
				closeDropdown();
			};

			// When the SearchTextField receives focus - simple API for multiple events that cause this
			const inputHasReceivedFocus = () => {
				if (!showDropdown) {
					fireAnalyticsScreenEvent(
						onDropdownViewed({
							commonAttributes,
						}),
					);
					setShowDropdown(true);
				}
			};

			return (
				<Box xcss={textFieldBoxStyles} ref={inputBoxRef}>
					<SearchTextField
						keyboardRef={inputRef}
						{...triggerProps}
						ref={triggerProps.ref}
						onSubmit={hasSubmitted}
						hasReceivedFocus={inputHasReceivedFocus}
						localQuery={localQuery}
						setLocalQuery={setLocalQuery}
					/>
				</Box>
			);
		},
		[
			localQuery,
			closeDropdown,
			showDropdown,
			fireAnalyticsScreenEvent,
			commonAttributes,
			setShowDropdown,
		],
	);

	const content = (
		<KeyboardHighlightProvider listenerNode={inputRef?.current}>
			<SearchDropdown
				inputWidth={inputBoxRef.current?.offsetWidth || 0}
				setLocalQuery={setLocalQuery}
			/>
		</KeyboardHighlightProvider>
	);

	return (
		<Popup
			shouldReturnFocus={false}
			content={() => content}
			isOpen={showDropdown}
			autoFocus={false}
			placement="bottom-start"
			trigger={trigger}
			onClose={closeDropdown}
		/>
	);
};

const FallbackInput = () => {
	const [localQuery, setLocalQuery] = useState('');

	return <SearchTextField localQuery={localQuery} setLocalQuery={setLocalQuery} />;
};

const InputWithErrorBoundary = () => {
	return (
		<ErrorBoundary fallback={<FallbackInput />} section="SearchPageInput">
			<UFOSegment name="search-page-input">
				<Input />
			</UFOSegment>
		</ErrorBoundary>
	);
};

export default InputWithErrorBoundary;
